/* eslint-disable prettier/prettier */
import React, { memo, useRef, useState } from 'react';
import { useIntl } from 'umi';
import { useDeepCompareEffect } from 'ahooks';
import classnames from 'classnames';
import { noop  } from '@/pages/home/utils/utils';
import { IImagesUploaderBtnProps } from '@/pages/home/components/ImagesUploader/types';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { validateUploadFilesWithErrorNotice } from '@gp/utils/file';
import StatusImage from './components/StatusImage';
import useCos from '@gp/hooks/useCos';
import { file2DataURL } from '@home/utils/file';
import type { IUploadStatus, ICover } from '@gp/components/CoverUploader/types';
import './index.scss';
import { COMMON_IMAGE_ACCEPTS } from '@gp/constants/const';
import { getMimeType } from '@gp/components/Upload';
import BACKGROUND_IMG_DEFAULT_ICON from '@/pages/home/assets/img/common/video-default.png';
import { isEmpty } from 'lodash-es';

// const PLACEHOLDER_COVER = BACKGROUND_IMG_DEFAULT_ICON;
// const PLACEHOLDER_COVER = '//imgcache.qq.com/open_proj/proj_qcloud_v2/tea-style/dist/assets/image/upload-thumb.png';
const EMPTY_META = {
  name: '',
  cover: '',
  path: '',
  file_size: Number.NaN,
  timestamp: Number.NaN,
} as any;
const ACCEPTS = COMMON_IMAGE_ACCEPTS;
const ACCEPTS_STR = ACCEPTS.join(', ');
const DEFAULT_MAX_SIZE = 500 * 1024 * 1024;

const ImageUploadBtnGp: React.FC<IImagesUploaderBtnProps> = (props, ref) => {
  const { formatMessage } = useIntl();
  const {
    disabled = false,
    // 接受的图片格式
    accept = ACCEPTS_STR,
    limitSize = DEFAULT_MAX_SIZE,
    errorImg = BACKGROUND_IMG_DEFAULT_ICON,
    svgIconType = 'cross',
    className,
    // 最多上传图片数量
    max = 1,
    // 是否允许批量上传
    multiple = true,
    // 是否启用上传进度功能
    // enabledProgress = true,
    // 是否启用图片压缩功能
    // enabledCompress = true,
    // 启用图片压缩时，预计最大接受的图片大小，单位为B
    // maxSize = DEFAULT_MAX_SIZE,
    // 上传模式为COS时，取消是否自动从COS删除本次临时上传的图片
    // autoDeleteWhenCancel = true,
    // 上传模式为COS时，文件存储路径
    cosPath = 'jpg',
    // 图片压缩报错提示文案
    // compressErrorMsg = formatMessage({
    //   id: 'dbh_compress_image_error_msg',
    //   defaultMessage: 'Image compress error',
    // }),
    // 图片上传网络报错提示文案
    // networkErrorMsg = formatMessage({
    //   id: 'dbh_network_error_msg',
    //   defaultMessage: 'Network error',
    // }),
    // 未知异常报错提示文案
    // unknowErrorMsg = formatMessage({
    //   id: 'dbh_unknow_error_msg',
    //   defaultMessage: 'Unknow error',
    // }),
    // 上传成功回调，通过该回调获取 图片数据
    onUploadSuccess = noop,
    onChange = noop,
    // 删除图片
    deleteOutside = noop,
    // 删除成功回调，通过该回调 更新 父组件的图片数据
    // onDeleteSuccess = noop,
    // 图片唯一标识key
    // uniqueKey,
    // 编辑状态图片回显
    editImagesData,
    // ...rest
  } = props;

  const fileInput = useRef<HTMLInputElement>(null);
  const { putObject, getObjectUrl } = useCos({qCouldServiceParams: cosPath});

  // cover的dataURL，用于页面展示
  const [cover, setCover] = useState(editImagesData?.cover || '');
  // const [uploading, setUploading] = useState(false);
  // cover的元信息，其中cover是保存COS返回的图片访问链接，需要回传给组件调用者
  const [meta, setMeta] = useState<ICover>(EMPTY_META);

  /**
     * 上传状态
     * none 空（代表没上传，空态）
     * uploading 正在上传中（已读取完本地文件，可以用dataURL形式展示图片）
     * success 已上传到服务器
     * error 上传失败
     */
  const [status, setStatus] = useState<IUploadStatus>(editImagesData?.cover ? 'success':'none');

  useDeepCompareEffect(() => {
    if (editImagesData && editImagesData?.loading !== true) {
      setMeta({
        ...meta,
        path: editImagesData?.cloud_path || '',
      });
      setCover(editImagesData?.cover || '');
      setStatus(editImagesData?.cover ? 'success':'none');
    }
  }, [editImagesData]);


  // 上传进度
  const [percents, setPercents] = useState(0);
  // 上传任务id
  const taskId = useRef('');

  // 上传进度更新处理
  const handleProgress = ({ percent }: { percent: number; }) => {
    const percentsTmp = Math.round(percent * 100);
    setPercents(percentsTmp);
  };

  const transferFiles2FileArray = (files: FileList | null): File[] => {
    if (!files) return [];
    return Array.prototype.slice.call(files, 0);
  };

  // 图片通过校验，上传COS
  const submit = async (files: File[]) => {
    if (!Array.isArray(files) || !files.length) return;
    let metaTmp: ICover = {
      name: '',
      cover: '',
      path: '',
      file_size: Number.NaN,
      timestamp: Number.NaN,
    };
    try {
      const file = files[0];
      const { name, size } = file;
      // 解析本地图片，获取File对象
      const fileDataUrl = await file2DataURL(file);
      const path = `${cosPath}/${Date.now()}fileName=${name}`; // 这个path很重要，可以凭借它去腾讯云拿链接
      metaTmp = {
        ...meta,
        name,
        path,
        file_size: size,
        timestamp: new Date().getTime(),
      };
      setCover(fileDataUrl);
      setMeta(metaTmp);
      setStatus('uploading');
      onChange?.(true);
      // setUploading(true);
      // 上传到COS
      await putObject({
        path,
        file,
        onProgress: handleProgress,
        onTaskReady: (id: string) => {
          taskId.current = id;
        },
      });
      // 从COS获取刚上传的图片的链接
      const res = await getObjectUrl({ path });
      metaTmp = { ...metaTmp, cover: res?.url || '' };
      setMeta(metaTmp);
      setStatus('success');
      onUploadSuccess?.(metaTmp);
    } catch (exception) {
      metaTmp = { ...metaTmp, cover: '' };
      setCover(errorImg);
      setMeta(metaTmp);
      setStatus('error');
      onChange?.(false);
    } finally {
      setPercents(Number.NaN);
      taskId.current = '';
      // setUploading(false);
    }
  };

  // 图片校验失败的逻辑
  const handleValidateError = (files: File[]) => {
    if (!Array.isArray(files) || !files.length) return;
    const { name, size } = files[0];
    const metaTmp = {
      name,
      cover: '',
      file_size: size,
      timestamp: new Date().getTime(),
    } as any;
    setCover(errorImg);
    setMeta(metaTmp);
    setStatus('none');
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const originalFiles = e?.target?.files;
    const isValid = validateUploadFilesWithErrorNotice(originalFiles, formatMessage, {
      multiple,
      max,
      accept: ACCEPTS,
      limitSize,
      getMimeType,
    });
    if (!isValid) {
      handleValidateError?.(transferFiles2FileArray(originalFiles));
      // REMARK: 注意这一行清空input的value的代码  一定要在回传FileList数据之后 再执行，否则会导致组件调用者获取不到FileList数据
      fileInput.current!.value = '';
      return;
    }
    submit(transferFiles2FileArray(e?.target?.files));
    fileInput.current!.value = '';
  };

  // 清除所有状态
  const clearAll = () => {
    setCover('');
    setMeta(EMPTY_META);
    setStatus('none');
    setPercents(0);
    setCover('');
    setCover('');
  };

  const handleDelete = (metaData: any) => {
    // if(status === 'success' && metaData?.path) {
    //   deleteObject({ path: metaData?.path });
    // }
    clearAll();
    deleteOutside?.();
  };

  return (
    <div className={classnames('dbg-image-upload-btn',
      {'dbg-image-upload-btn-has-img': !isEmpty(cover)},
      className,
    )}
    >
      {status !== 'none' && (<div className="dbg-image-upload-btn-content">
        <StatusImage
          errorImg={errorImg}
          status={status}
          percents={percents}
          tips={'tips'}
          cover={cover}
          metaData={meta}
          onDelete={handleDelete}
        />
      </div>)}
      {status === 'none' && (
        <div
          className={classnames('dbg-image-upload-btn-loading',
            {
              'dbg-image-upload-btn-div-disabled': disabled,
            },
          )}
          style={{ position: 'relative' }}
        >
          <SvgIcon type={svgIconType} className="dbg-image-upload-add-icon" />
          <input
            ref={fileInput}
            type="file"
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              opacity: 0,
            }}
            disabled={disabled}
            multiple={multiple}
            accept={accept}
            onChange={handleFileInputChange}
          />
        </div>
      )}
    </div>
  );
};

export default memo(ImageUploadBtnGp);
